// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/utils/incidentReclassification.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/utils/incidentReclassification.js
 *
 * The approved re-classification record, as the browser shows it (msr 4.6b,
 * phase4-plan D18; criteria-p4.md §3.4 rule 8). The server is the authority:
 * azure-functions/shared/incident/reclassification.js decides what can be
 * requested, who decides, and what IRR-03 honours. This file only reads the
 * stored record the same way, so the obligation tab, the Action Centre and
 * the metric never tell a person three different things.
 *
 * A regime the classifier re-classified as not notifiable AFTER its first
 * notification deadline passed is not "not applicable": IRR-03 still counts
 * the incident as a missed notification until a named approver approves a
 * re-classification record. That state is shown as "re-classified after
 * deadline" (co-owner review of the 4.6b plan).
 */

// The shortest version 2 notification period per regime, in hours
// (incidentClassifier.js DEADLINE_RULES: GDPR 72 h; NIS2 and CRA 24 h early
// warning). azure-functions/__tests__/incidents-reclassification.test.js
// pins the server side to the same values.
const FIRST_NOTIFICATION_HOURS = Object.freeze({ gdpr: 72, nis2: 24, cra: 24 });
const RECLASSIFIABLE_REGIMES = Object.freeze(Object.keys(FIRST_NOTIFICATION_HOURS));

const HOUR_MS = 3_600_000;
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function historyOf(ob) {
  return Array.isArray(ob?.thresholdHistory)
    ? ob.thresholdHistory.filter((h) => h && !Number.isNaN(Date.parse(h.at)))
    : [];
}

function thresholdAt(ob, ms) {
  if (!isObject(ob)) return null;
  const history = historyOf(ob);
  if (history.length) {
    let value = typeof history[0].from === 'boolean' ? history[0].from : null;
    for (const h of history) {
      if (Date.parse(h.at) > ms) break;
      value = typeof h.to === 'boolean' ? h.to : null;
    }
    return value;
  }
  return typeof ob.thresholdMet === 'boolean' ? ob.thresholdMet : null;
}

function inForceAt(ob, ms) {
  let found = null;
  for (const h of historyOf(ob)) {
    if (Date.parse(h.at) > ms) break;
    found = h;
  }
  return found;
}

/** Whether the regime is re-classified as not notifiable after its first deadline passed. */
function isDeclassifiedAfterDeadline(incident, key, nowMs = Date.now()) {
  const ob = incident?.frameworkObligations?.[key];
  if (!isObject(ob) || ob.thresholdMet !== false || !FIRST_NOTIFICATION_HOURS[key]) return false;
  if (thresholdAt(ob, nowMs) === true) return false;
  const clock = Date.parse(incident.discoveredAt);
  if (!Number.isNaN(clock)) {
    const first = clock + FIRST_NOTIFICATION_HOURS[key] * HOUR_MS;
    if (first <= nowMs && thresholdAt(ob, first) === true) return true;
  }
  const stamp = ob.notifiableAtDeadline;
  return Boolean(stamp && !Number.isNaN(Date.parse(stamp.at)) && Date.parse(stamp.at) <= nowMs);
}

function reclassificationsOf(incident, key) {
  const list = incident?.frameworkObligations?.[key]?.reclassifications;
  return Array.isArray(list) ? list.filter(isObject) : [];
}

const isPendingRequest = (r) => !isObject(r?.decision) && !isObject(r?.withdrawn);

/** The request's state for display: 'pending' | 'approved' | 'rejected' | 'withdrawn'. */
function requestStatus(r) {
  if (isObject(r?.withdrawn)) return 'withdrawn';
  if (isObject(r?.decision)) return r.decision.outcome;
  return 'pending';
}

/** The approved record in force now (the one IRR-03 honours), or null. */
function honouredRequest(incident, key, nowMs = Date.now()) {
  const ob = incident?.frameworkObligations?.[key];
  if (thresholdAt(ob, nowMs) !== false) return null;
  const current = inForceAt(ob, nowMs);
  if (current && current.to !== false) return null;
  const at = current ? current.at : null;
  // The earliest approval, as the server reads it.
  let honoured = null;
  for (const r of reclassificationsOf(incident, key)) {
    if (r.decision?.outcome !== 'approved') continue;
    const decided = Date.parse(r.decision.at);
    if (!(decided <= nowMs) || (r.classificationAt || null) !== at) continue;
    if (!honoured || decided < Date.parse(honoured.decision.at)) honoured = r;
  }
  return honoured;
}

/** The person whose edit turned the classification to false, or null. */
function classifiedBy(incident, key, nowMs = Date.now()) {
  return inForceAt(incident?.frameworkObligations?.[key], nowMs)?.by || null;
}

/**
 * Pending re-classification requests the user is the named approver of —
 * the Action Centre count and list read this one predicate.
 */
function listReclassificationsAwaitingUser(state, userId) {
  if (!userId) return [];
  const out = [];
  for (const incident of state?.incidents || []) {
    if (!incident || typeof incident !== 'object') continue;
    for (const key of RECLASSIFIABLE_REGIMES) {
      for (const request of reclassificationsOf(incident, key)) {
        if (
          isPendingRequest(request) &&
          request.approverId === userId &&
          request.requestedBy !== userId
        ) {
          out.push({ incident, regime: key, request });
        }
      }
    }
  }
  return out;
}

module.exports = {
  FIRST_NOTIFICATION_HOURS,
  RECLASSIFIABLE_REGIMES,
  isDeclassifiedAfterDeadline,
  reclassificationsOf,
  isPendingRequest,
  requestStatus,
  honouredRequest,
  classifiedBy,
  listReclassificationsAwaitingUser,
};
